import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react'
import { Segmented } from 'antd'
import { KPISummary } from './kpi-summary/kpi-summary'
import { NotificationListView } from './notification-list-view/notification-list-view'
import { useDomainEffect } from '../hooks/use-domain-effect'
import { usePullToRefresh } from '../hooks/use-pull-to-refresh'
import './mobile-home.scss'

type DashboardTab = 'notifications' | 'drafts'

const VALID_TABS: DashboardTab[] = ['notifications', 'drafts']

function formatTimeAgo(date: Date): string {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
    if (seconds < 5) return 'Updated just now'
    if (seconds < 60) return `Updated ${seconds}s ago`
    const minutes = Math.floor(seconds / 60)
    return `Updated ${minutes}m ago`
}

export const MobileHome: React.FC = observer(() => {
    const history = useHistory()
    const location = useLocation()
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
    const [timeAgoLabel, setTimeAgoLabel] = useState<string>('')
    const kpiRefreshRef = useRef<(() => void) | null>(null)
    const listRefreshRef = useRef<(() => void) | null>(null)

    // Derive active tab from URL query param
    const queryTab = new URLSearchParams(location.search).get('tab') as DashboardTab | null
    const activeTab: DashboardTab = queryTab && VALID_TABS.includes(queryTab) ? queryTab : 'notifications'

    useDomainEffect(() => {
        setLastUpdated(null)
        setTimeAgoLabel('')
    })

    useEffect(() => {
        if (!lastUpdated) {
            setTimeAgoLabel('')
            return
        }

        setTimeAgoLabel(formatTimeAgo(lastUpdated))

        const interval = setInterval(() => {
            setTimeAgoLabel(formatTimeAgo(lastUpdated))
        }, 5000)

        return () => clearInterval(interval)
    }, [lastUpdated])

    const handleKPIUpdate = useCallback((date: Date, refresh: () => void) => {
        setLastUpdated(date)
        kpiRefreshRef.current = refresh
    }, [])

    const handlePullRefresh = useCallback(() => {
        kpiRefreshRef.current?.()
        listRefreshRef.current?.()
    }, [])

    const { isPulling, handlers: pullHandlers } = usePullToRefresh({ onRefresh: handlePullRefresh })

    const handleTabChange = useCallback(
        (value: string | number) => {
            const tab = value as DashboardTab
            const params = new URLSearchParams(location.search)
            if (tab === 'notifications') {
                params.delete('tab')
            } else {
                params.set('tab', tab)
            }
            const search = params.toString()
            history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' })
        },
        [history, location.pathname, location.search],
    )

    const tabOptions = [
        { label: 'Notifications', value: 'notifications' as DashboardTab },
        { label: 'Drafts', value: 'drafts' as DashboardTab },
    ]

    return (
        <div className="mobile-home" {...pullHandlers}>
            <KPISummary onUpdate={handleKPIUpdate} />

            <div className="mobile-home__updated" aria-live="polite">
                {isPulling ? 'Release to refresh' : timeAgoLabel}
            </div>

            <Segmented
                options={tabOptions}
                value={activeTab}
                onChange={handleTabChange}
                block
                className="mobile-home__tabs"
            />

            {activeTab === 'notifications' && (
                <NotificationListView mode="notifications" onRefreshRef={listRefreshRef} />
            )}

            {activeTab === 'drafts' && <NotificationListView mode="drafts" onRefreshRef={listRefreshRef} />}
        </div>
    )
})

MobileHome.displayName = 'MobileHome'
